// Szegmensek (UI-4): a country's segments, the way Klaviyo lists them —
// name (with what it selects, in one line), state, size with the moment it
// was counted, and the campaigns that use it — and the actions a row allows.
//
// A country holds tens of segments, not millions, so the page loads them all
// (the API's page of 500) and searches and sorts on the client; the list
// endpoint has no search of its own to call.
//
// The Klaviyo engagement segments (3.2) have a tab of their own, every
// country at once, grouped (`KlaviyoSegments`): their members come from
// Klaviyo and none of the actions here applies to them.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusIcon, SearchIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  archiveSegment,
  countSegment,
  fetchContactLists,
  fetchKlaviyoSegments,
  fetchSegmentUsage,
  fetchSegments,
  materialiseSegment,
} from "@/lib/api";
import type { Segment, SegmentUse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { PageHeader } from "@/components/emc/PageHeader";
import { RowActions } from "@/components/emc/RowActions";
import type { RowAction } from "@/components/emc/RowActions";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import { OverlapPanel } from "@/pages/segments/OverlapPanel";
import { KlaviyoSegments } from "@/pages/segments/KlaviyoSegments";
import { oursOnly } from "@/pages/segments/klaviyo";
import { COUNTRIES, SEGMENT_PAGE, messageOf } from "@/pages/segments/data";
import { engineLine } from "@/pages/segments/EngineError";
import { ago } from "@/pages/segments/format";
import { summarize } from "@/pages/segments/model";
import { Country } from "@/components/emc/Flag";

const REFRESH_MS = 3_000;
const SEARCH_DELAY_MS = 250;
const BUILDING = new Set(["requested", "materialising"]);
const REBUILDABLE = new Set(["defined", "ready", "stale", "failed"]);

function fold(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

export default function SegmentList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const asked = (params.get("orszag") ?? "").toUpperCase();
  const country = COUNTRIES.includes(asked) ? asked : "HU";
  const view = params.get("nezet");
  const archived = view === "archivalt";
  const klaviyo = view === "klaviyo";
  const q = params.get("q") ?? "";
  const [typed, setTyped] = useState(q);
  const [segments, setSegments] = useState<Segment[] | null>(null);
  const [uses, setUses] = useState<SegmentUse[]>([]);
  const [listNames, setListNames] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<Segment | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [klaviyoCount, setKlaviyoCount] = useState<number | null>(null);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setSegments(null);
      try {
        const [rows, used] = await Promise.all([
          fetchSegments(country, SEGMENT_PAGE),
          fetchSegmentUsage(country).catch(() => [] as SegmentUse[]),
        ]);
        setSegments(rows);
        setUses(used);
        setError(null);
        setNow(Date.now());
      } catch (raised: unknown) {
        setError(messageOf(raised));
        if (!quiet) setSegments([]);
      }
    },
    [country],
  );

  useEffect(() => {
    void load();
    fetchContactLists(country)
      .then((lists) => setListNames(new Map(lists.map((item) => [String(item.id), item.name]))))
      .catch(() => setListNames(new Map()));
  }, [load, country]);

  // The Klaviyo tab's number, every country's: tens of rows.
  useEffect(() => {
    fetchKlaviyoSegments()
      .then((answer) => setKlaviyoCount(answer.length))
      .catch(() => setKlaviyoCount(null));
  }, []);

  // A build in flight: the list follows it.
  const moving = segments?.some((segment) => BUILDING.has(segment.state)) ?? false;
  useEffect(() => {
    if (!moving) return undefined;
    const timer = window.setInterval(() => void load(true), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [moving, load]);

  // The search waits for the typing to stop, and lives in the address.
  useEffect(() => {
    if (typed === q) return undefined;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (typed.trim()) next.set("q", typed.trim());
      else next.delete("q");
      setParams(next, { replace: true });
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [typed, q, params, setParams]);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  const names = useMemo(
    () => ({
      lists: listNames,
      segments: new Map((segments ?? []).map((segment) => [String(segment.id), segment.name])),
    }),
    [listNames, segments],
  );

  const usesBySegment = useMemo(() => {
    // The campaign that sends to a segment first, the newest first after it:
    // "who is this segment for" before "who leaves it out".
    const ordered = [...uses].sort(
      (a, b) => Number(a.role !== "audience") - Number(b.role !== "audience") || b.campaign_id - a.campaign_id,
    );
    const map = new Map<number, SegmentUse[]>();
    for (const use of ordered) map.set(use.segment_id, [...(map.get(use.segment_id) ?? []), use]);
    return map;
  }, [uses]);

  const counts = useMemo(() => {
    const all = oursOnly(segments ?? []);
    return {
      active: all.filter((segment) => segment.state !== "archived").length,
      archived: all.filter((segment) => segment.state === "archived").length,
    };
  }, [segments]);

  const rows = useMemo(() => {
    const needle = fold(q.trim());
    return oursOnly(segments ?? [])
      .filter((segment) => (segment.state === "archived") === archived)
      .filter((segment) => needle === "" || fold(segment.name).includes(needle))
      .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }, [segments, archived, q]);

  async function run(label: string, action: () => Promise<unknown>, done: string) {
    try {
      await action();
      toast.success(done, { description: label });
      await load(true);
    } catch (raised: unknown) {
      toast.error("Nem sikerült", { description: engineLine(messageOf(raised), names.segments) });
    }
  }

  const columns: ColumnDef<Segment>[] = [
    {
      id: "name",
      header: "Név",
      cell: ({ row }) => {
        const summary = summarize(row.original.definition, names);
        return (
          <span className="flex max-w-120 min-w-0 flex-col">
            <Link
              to={`/szegmensek/${row.original.id}`}
              className="font-medium text-ink hover:text-blue-ink"
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <span className="truncate text-label text-muted-foreground" title={summary}>
              {summary}
            </span>
          </span>
        );
      },
      meta: { wrap: true },
    },
    { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
    {
      id: "kind",
      header: "Típus",
      cell: ({ row }) => labelOf("segmentKind", row.original.kind),
      meta: { muted: true },
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => <StatusPill domain="segment" state={row.original.state} />,
    },
    {
      id: "members",
      header: "Tagok",
      cell: ({ row }) => {
        const segment = row.original;
        if (segment.kind === "static" && BUILDING.has(segment.state)) {
          return <span className="text-muted-foreground">épül…</span>;
        }
        return (
          <span className="flex flex-col items-end">
            <span>{count(segment.member_count)}</span>
            <span className="text-label text-muted-foreground">
              {segment.counted_at ? `számolva ${ago(segment.counted_at, now)}` : "nincs számolva"}
            </span>
          </span>
        );
      },
      meta: { align: "right" },
    },
    {
      id: "uses",
      header: "Használja",
      cell: ({ row }) => {
        const mine = usesBySegment.get(row.original.id) ?? [];
        if (mine.length === 0) return <span className="text-muted-foreground">—</span>;
        const first = mine[0]!;
        return (
          <span className="flex flex-col" title={mine.map((use) => use.campaign_name).join(", ")}>
            <span className="truncate">{first.campaign_name}</span>
            <span className="text-label text-muted-foreground">
              {mine.length > 1 ? `és még ${mine.length - 1} kampány` : first.role === "exclude" ? "kizárásként" : "közönségként"}
            </span>
          </span>
        );
      },
      meta: { wrap: true },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) => {
        const segment = row.original;
        const actions: RowAction[] = [
          { label: "Megnyitás", onSelect: () => navigate(`/szegmensek/${segment.id}`) },
        ];
        if (segment.kind === "dynamic" && segment.state !== "archived") {
          actions.push({
            label: "Újraszámolás",
            onSelect: () => void run(segment.name, () => countSegment(segment.id), "Újraszámolva"),
          });
        }
        if (segment.kind === "static" && REBUILDABLE.has(segment.state)) {
          actions.push({
            label: segment.state === "defined" ? "Felépítés" : "Újraépítés",
            onSelect: () =>
              void run(segment.name, () => materialiseSegment(segment.id), "A tagság építése elindult"),
          });
        }
        actions.push({ label: "Másolat", onSelect: () => navigate(`/szegmensek/uj?masolat=${segment.id}`) });
        if (segment.state !== "archived") {
          actions.push({
            label: "Archiválás",
            danger: true,
            disabled: BUILDING.has(segment.state),
            onSelect: () => setArchiving(segment),
          });
        }
        return <RowActions actions={actions} label={`${segment.name}: műveletek`} />;
      },
      meta: { align: "right" },
    },
  ];

  const newHref = `/szegmensek/uj?orszag=${country}`;

  return (
    <>
      <PageHeader
        title="Szegmensek"
        description="Feltételekből álló közönségek, országonként. A kampány a küldés pillanatában a leiratkozottakat és a kizárt címeket mindig kiveszi — a szegmens ezt nem tudja megkerülni."
        actions={
          <Button asChild>
            <Link to={newHref}>
              <PlusIcon />
              Új szegmens
            </Link>
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <Tabs
          value={klaviyo ? "klaviyo" : archived ? "archivalt" : "aktiv"}
          onValueChange={(value) => setParam("nezet", value === "aktiv" ? null : value)}
        >
          <TabsList aria-label="Nézet">
            <TabsTrigger value="aktiv">
              Aktív
              {segments !== null && <span className="text-label text-muted-foreground">{count(counts.active)}</span>}
            </TabsTrigger>
            <TabsTrigger value="archivalt">
              Archivált
              {segments !== null && <span className="text-label text-muted-foreground">{count(counts.archived)}</span>}
            </TabsTrigger>
            <TabsTrigger value="klaviyo">
              Klaviyo-szegmensek
              {klaviyoCount !== null && <span className="text-label text-muted-foreground">{count(klaviyoCount)}</span>}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Toolbar>
          {!klaviyo && (
            <NativeSelect aria-label="Ország" value={country} onChange={(event) => setParam("orszag", event.target.value)}>
              {COUNTRIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </NativeSelect>
          )}
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Keresés név szerint"
              placeholder="Keresés név szerint"
              className="w-64 pl-8"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
        </Toolbar>
      </div>

      {klaviyo && <KlaviyoSegments needle={q} onCount={setKlaviyoCount} />}

      {!klaviyo && error !== null && <ErrorState message={error} className="mb-4" onRetry={() => void load()} />}

      {!klaviyo && (
        <DataTable
          label="Szegmensek"
          columns={columns}
          data={rows}
          loading={segments === null}
          getRowId={(segment) => String(segment.id)}
          onRowClick={(segment) => navigate(`/szegmensek/${segment.id}`)}
          empty={
            <EmptyState
              bare
              title={
                q
                  ? `Nincs „${q}” nevű szegmens.`
                  : archived
                    ? "Nincs archivált szegmens ebben az országban."
                    : "Még nincs szegmens ebben az országban."
              }
              description={
                q || archived
                  ? undefined
                  : "Kezdd egy warmup-hullámmal vagy egy gyakori Klaviyo-szegmenssel — a feltételek utána átírhatók."
              }
              action={
                !q && !archived ? (
                  <Button asChild>
                    <Link to={newHref}>
                      <PlusIcon />
                      Új szegmens
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          }
          footer={
            rows.length > 0 ? <TablePager summary={`${count(rows.length)} szegmens · ${country}`} /> : undefined
          }
        />
      )}

      {!archived && !klaviyo && (segments?.filter((segment) => segment.state !== "archived").length ?? 0) > 1 && (
        <div className="mt-6">
          <OverlapPanel segments={(segments ?? []).filter((segment) => segment.state !== "archived")} />
        </div>
      )}

      <ActionDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        tone="danger"
        title="A szegmens archiválása"
        description="Az archivált szegmens nem szerkeszthető és nem építhető újra, és a kampány-varázsló sem kínálja fel. A már elküldött kampányokat nem érinti. Ha egy másik szegmens hivatkozik rá, előbb azt kell módosítani."
        confirmLabel="Archiválás"
        onConfirm={async () => {
          if (archiving === null) return;
          await archiveSegment(archiving.id).catch((raised: unknown) => {
            throw new Error(engineLine(messageOf(raised), names.segments));
          });
          toast.success("A szegmens archiválva", { description: archiving.name });
          await load(true);
        }}
      />
    </>
  );
}
